import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Send, FileText, Copy, Check, Lock, TrendingUp,
  Users, Wallet, BadgeEuro, ArrowRight, Sparkles, Clock, CheckCircle2, XCircle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePromoterStatus } from '../hooks/usePromoterStatus';
import toast from 'react-hot-toast';

interface DashboardLead {
  leadId: string;
  festivalName: string;
  contactName: string;
  contactEmail: string;
  status: string;
  createdAt: string | null;
  commissionGenerated: number;
}
interface DashboardTx {
  txId: string;
  festivalId: string;
  commission: number;
  grossRevenue: number;
  status: string;
  transactionDate: string | null;
}
interface DashboardData {
  promoter: { firstName: string; lastName: string; affiliateCode: string | null; codeActive: boolean; contractUrl: string | null };
  kpis: { leadsSubmitted: number; activeFestivals: number; totalDue: number; totalPaid: number };
  payableBalance: number;
  payoutThreshold: number;
  billable: boolean;
  leads: DashboardLead[];
  transactions: DashboardTx[];
}

// Statuts de lead → libellé + couleur.
const LEAD_STATUS: Record<string, { label: string; cls: string; icon: any }> = {
  lead_submitted:       { label: 'Invitation envoyée', cls: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: Clock },
  organizer_registered: { label: 'Inscrit',            cls: 'bg-sky-500/10 text-sky-400 border-sky-500/20',       icon: CheckCircle2 },
  first_deal:           { label: 'Premier deal 🎉',    cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: TrendingUp },
  inactive:             { label: 'Inactif',            cls: 'bg-white/5 text-gray-500 border-white/10',           icon: XCircle },
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
}

export default function PromoterDashboardPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const promoter = usePromoterStatus();

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const token = await user.getIdToken();
      const resp = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/promoters/${user.uid}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await resp.json();
      if (!resp.ok) throw new Error(j.error || 'Erreur');
      setData(j);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const copyCode = () => {
    if (!data?.promoter.affiliateCode) return;
    navigator.clipboard.writeText(data.promoter.affiliateCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
    toast.success('Code affilié copié');
  };

  const requestPayout = async () => {
    if (!user) return;
    setRequesting(true);
    try {
      const token = await user.getIdToken();
      const resp = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/promoters/${user.uid}/request-payout`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const j = await resp.json();
      if (!resp.ok) throw new Error(j.error || 'Erreur');
      toast.success('Demande de versement envoyée à l\'équipe Festin 🎉');
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setRequesting(false);
    }
  };

  if (authLoading || promoter.loading || loading) {
    return <div className="min-h-screen bg-[#0D0D0F] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#FF5C00]/20 border-t-[#FF5C00] rounded-full animate-spin" />
    </div>;
  }

  // Garde : codeActive requis.
  if (!user || !promoter.codeActive) {
    return (
      <div className="min-h-screen bg-[#0D0D0F] text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <Lock className="w-8 h-8 text-[#FF5C00] mx-auto mb-4" />
          <h1 className="text-2xl font-black mb-3">Accès réservé aux promoteurs actifs</h1>
          <p className="text-gray-400 text-sm mb-6">
            {!user
              ? 'Connectez-vous avec votre compte promoteur.'
              : promoter.hasProfile
              ? 'Votre code affilié doit être activé (contrat signé) pour accéder au dashboard.'
              : 'Vous n\'êtes pas encore promoteur. Rejoignez le programme d\'affiliation.'}
          </p>
          <button
            onClick={() => navigate(promoter.hasProfile ? '/promoter/sign' : '/become-promoter')}
            className="bg-[#FF5C00] hover:bg-[#e05000] text-white px-8 py-3.5 rounded-2xl font-black text-sm transition-all"
          >
            {promoter.hasProfile ? 'Signer mon contrat' : 'Devenir promoteur'}
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { kpis, payableBalance, payoutThreshold, billable } = data;
  const progressPct = Math.min(100, Math.round((payableBalance / payoutThreshold) * 100));

  const kpiCards = [
    { label: 'Leads soumis', value: kpis.leadsSubmitted, icon: Send, color: 'text-sky-400' },
    { label: 'Festivals actifs', value: kpis.activeFestivals, icon: Users, color: 'text-emerald-400' },
    { label: 'Commission due', value: `${kpis.totalDue}€`, icon: BadgeEuro, color: 'text-[#FF5C00]' },
    { label: 'Commission versée', value: `${kpis.totalPaid}€`, icon: Wallet, color: 'text-violet-400' },
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0F] text-white">
      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#FF5C00]/10 border border-[#FF5C00]/20 text-[#FF5C00] text-xs font-black px-3 py-1.5 rounded-full mb-4">
              <LayoutDashboard className="w-3.5 h-3.5" /> DASHBOARD PROMOTEUR
            </div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight">Bonjour {data.promoter.firstName} 👋</h1>
          </div>
          {/* Code affilié + copie */}
          <button onClick={copyCode}
            className="flex items-center gap-3 bg-[#161618] border border-[#FF5C00]/20 rounded-2xl px-4 py-3 hover:border-[#FF5C00]/40 transition-all group">
            <Sparkles className="w-5 h-5 text-[#FF5C00] shrink-0" />
            <div className="text-left">
              <p className="text-[10px] uppercase tracking-widest text-gray-500">Votre code affilié</p>
              <p className="text-base font-black text-[#FF5C00]">{data.promoter.affiliateCode}</p>
            </div>
            {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-gray-500 group-hover:text-white transition-colors" />}
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {kpiCards.map((k) => (
            <div key={k.label} className="bg-[#161618] border border-white/8 rounded-2xl p-5">
              <k.icon className={`w-5 h-5 ${k.color} mb-3`} />
              <p className="text-2xl font-black">{k.value}</p>
              <p className="text-xs text-gray-500 mt-1">{k.label}</p>
            </div>
          ))}
        </div>

        {/* Bloc paiement */}
        <div className={`rounded-2xl p-6 mb-8 border ${billable ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-[#161618] border-white/8'}`}>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Solde en attente de facturation</p>
              <p className={`text-3xl font-black ${billable ? 'text-emerald-400' : 'text-gray-300'}`}>{payableBalance}€</p>
            </div>
            {billable ? (
              <button onClick={requestPayout} disabled={requesting}
                className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white px-6 py-3 rounded-2xl font-black text-sm transition-all">
                {requesting ? 'Envoi…' : 'Demander le versement'}
                {!requesting && <ArrowRight className="w-4 h-4" />}
              </button>
            ) : (
              <div className="text-right">
                <p className="text-xs text-gray-500">Seuil de versement</p>
                <p className="text-sm font-black text-gray-400">{payoutThreshold}€</p>
              </div>
            )}
          </div>
          {!billable && (
            <>
              <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full bg-[#FF5C00] rounded-full transition-all" style={{ width: `${progressPct}%` }} />
              </div>
              <p className="text-[11px] text-gray-600 mt-2">
                Plus que <strong className="text-gray-400">{Math.max(0, Math.round((payoutThreshold - payableBalance) * 100) / 100)}€</strong> avant de pouvoir demander votre versement.
              </p>
            </>
          )}
        </div>

        {/* Tableau des leads */}
        <div className="bg-[#161618] border border-white/8 rounded-2xl overflow-hidden mb-8">
          <div className="px-5 py-4 border-b border-white/8">
            <h2 className="font-black text-sm">Mes leads ({data.leads.length})</h2>
          </div>
          {data.leads.length === 0 ? (
            <div className="px-5 py-10 text-center text-gray-500 text-sm">
              Aucun lead pour l'instant. Soumettez votre premier festival ci-dessous 👇
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500 border-b border-white/8">
                    <th className="px-5 py-3 font-black">Festival</th>
                    <th className="px-5 py-3 font-black">Contact</th>
                    <th className="px-5 py-3 font-black">Statut</th>
                    <th className="px-5 py-3 font-black">Date</th>
                    <th className="px-5 py-3 font-black text-right">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {data.leads.map((l) => {
                    const st = LEAD_STATUS[l.status] || LEAD_STATUS.lead_submitted;
                    return (
                      <tr key={l.leadId} className="border-b border-white/5 last:border-0">
                        <td className="px-5 py-4 font-bold">{l.festivalName}</td>
                        <td className="px-5 py-4 text-gray-400">{l.contactName}</td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border ${st.cls}`}>
                            <st.icon className="w-3 h-3" /> {st.label}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-gray-500">{fmtDate(l.createdAt)}</td>
                        <td className="px-5 py-4 text-right font-black text-[#FF5C00]">
                          {l.commissionGenerated > 0 ? `${l.commissionGenerated}€` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Actions rapides */}
        <div className="grid sm:grid-cols-3 gap-4">
          <button onClick={() => navigate('/promoter/submit-festival')}
            className="flex items-center gap-3 bg-[#FF5C00] hover:bg-[#e05000] text-white px-5 py-4 rounded-2xl font-black text-sm transition-all">
            <Send className="w-4 h-4" /> Soumettre un festival
          </button>
          {data.promoter.contractUrl ? (
            <a href={data.promoter.contractUrl} target="_blank" rel="noreferrer"
              className="flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-5 py-4 rounded-2xl font-black text-sm transition-all">
              <FileText className="w-4 h-4" /> Mon contrat
            </a>
          ) : (
            <div className="flex items-center gap-3 bg-white/5 border border-white/10 text-gray-600 px-5 py-4 rounded-2xl font-black text-sm">
              <FileText className="w-4 h-4" /> Contrat indisponible
            </div>
          )}
          <button onClick={copyCode}
            className="flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-5 py-4 rounded-2xl font-black text-sm transition-all">
            {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />} Copier mon code
          </button>
        </div>
      </div>
    </div>
  );
}
